/* Rangrays — Watch & Shop */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let eventsModule = null;
async function getEvents() {
  if (eventsModule !== null) return eventsModule;
  try { eventsModule = await import('@shopify/events'); } catch (e) { eventsModule = false; }
  return eventsModule;
}

function loadSrc(video) {
  if (video && !video.getAttribute('src') && video.dataset.src) {
    video.src = video.dataset.src;
  }
}
function safePlay(video, allowSoundFallback) {
  if (!video) return;
  const p = video.play();
  if (p && p.catch) {
    p.catch((err) => {
      if (err && err.name === 'NotAllowedError' && allowSoundFallback && !video.muted) {
        // browser blocked sound: play muted and let the mute button reflect it
        video.muted = true;
        video.play().catch(() => {});
        video.dispatchEvent(new CustomEvent('rrws:forced-mute', { bubbles: true }));
      }
    });
  }
}

class WatchShop {
  constructor(root) {
    this.root = root;
    // on product pages, move the section to sit right below the product block
    const moveAfter = root.dataset.rrwsMoveAfter;
    if (moveAfter) {
      const target = document.querySelector(moveAfter);
      const wrapper = root.closest('.shopify-section') || root;
      if (target && target.nextElementSibling !== wrapper) target.after(wrapper);
      root.classList.remove('rrws--pending');
    }
    this.track = root.querySelector('[data-rrws-track]');
    this.player = root.querySelector('[data-rrws-player]');
    if (!this.track || !this.player) return;
    this.feed = this.player.querySelector('[data-rrws-feed]');
    this.slides = Array.from(this.feed.querySelectorAll('.rrws-p__slide'));
    this.toastEl = this.player.querySelector('[data-rrws-toast]');
    this.muted = false;
    this.active = -1;
    this.lastFocus = null;

    // carry colours onto the player, then move it to <body> so fixed positioning is never trapped
    const cs = getComputedStyle(root);
    ['--rrws-bg', '--rrws-text', '--rrws-accent'].forEach((v) => {
      const val = cs.getPropertyValue(v).trim();
      if (val) this.player.style.setProperty(v, val);
    });
    document.body.appendChild(this.player);

    this.initCards();
    this.initArrows();
    this.initPlayer();
    this.openFromUrl();
  }

  /* ---------- carousel ---------- */
  initCards() {
    const vids = Array.from(this.track.querySelectorAll('.rrws__vid'));
    if ('IntersectionObserver' in window && !reduceMotion) {
      this.cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting && e.intersectionRatio >= 0.55) {
            loadSrc(v);
            if (this.player.hidden) safePlay(v);
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: [0, 0.55] });
      vids.forEach((v) => this.cardObserver.observe(v));
    } else {
      vids.forEach((v) => { v.preload = 'metadata'; loadSrc(v); });
    }
    this.track.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-rrws-open]');
      if (btn) this.open(Number(btn.dataset.rrwsOpen), btn);
    });
  }

  initArrows() {
    const prev = this.root.querySelector('[data-rrws-prev]');
    const next = this.root.querySelector('[data-rrws-next]');
    if (!prev || !next) return;
    const step = () => {
      const item = this.track.querySelector('.rrws__item');
      return item ? (item.getBoundingClientRect().width + 14) * 2 : 400;
    };
    const sync = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max;
      const hide = max <= 0;
      prev.parentElement.style.visibility = hide ? 'hidden' : '';
    };
    prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
    this.track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ---------- player ---------- */
  initPlayer() {
    this.slideObserver = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          this.activate(Number(e.target.dataset.index));
        }
      });
    }, { root: this.feed, threshold: [0.6] });
    this.slides.forEach((s) => this.slideObserver.observe(s));

    this.player.addEventListener('click', (e) => {
      if (e.target.closest('[data-rrws-close]')) return this.close();
      const stepBtn = e.target.closest('[data-rrws-step]');
      if (stepBtn) return this.go(this.active + Number(stepBtn.dataset.rrwsStep));
      if (e.target.closest('[data-rrws-mute]')) return this.toggleMute();
      if (e.target.closest('[data-rrws-share]')) return this.share();
      const atc = e.target.closest('[data-rrws-atc]');
      if (atc) return this.addToCart(atc);
      // tap on the video itself toggles play/pause
      if (e.target.classList.contains('rrws-p__shade') || e.target.classList.contains('rrws-p__vid')) {
        const v = this.slides[this.active]?.querySelector('video');
        if (v) v.paused ? safePlay(v, true) : v.pause();
      }
    });

    this.feed.addEventListener('rrws:forced-mute', () => {
      this.muted = true;
      this.slides.forEach((s) => s.querySelector('[data-rrws-mute]')?.setAttribute('aria-pressed', 'true'));
    });

    this.onKey = (e) => {
      if (this.player.hidden || document.querySelector('theme-drawer[open]')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); this.go(this.active + 1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); this.go(this.active - 1); }
      if (e.key === 'Tab') this.trapFocus(e);
    };
    this.onPop = () => { if (!this.player.hidden) this.close(true); };
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('popstate', this.onPop);
  }

  open(index, trigger) {
    this.lastFocus = trigger || document.activeElement;
    this.track.querySelectorAll('.rrws__vid').forEach((v) => v.pause());
    this.player.hidden = false;
    document.documentElement.classList.add('rrws-lock');
    const slide = this.slides[index] || this.slides[0];
    this.feed.scrollTop = slide.offsetTop;
    this.active = -1;
    this.activate(Number(slide.dataset.index));
    try { history.pushState({ rrws: true }, '', this.urlFor(slide)); } catch (e) {}
    this.pushed = true;
    const closeBtn = window.matchMedia('(max-width: 749px), (max-aspect-ratio: 10/16)').matches
      ? slide.querySelector('[data-rrws-close]')
      : this.player.querySelector('.rrws-p__close-desk');
    closeBtn?.focus({ preventScroll: true });
  }

  close(fromPop) {
    this.slides.forEach((s) => { const v = s.querySelector('video'); v.pause(); });
    cancelAnimationFrame(this.raf);
    this.player.hidden = true;
    document.documentElement.classList.remove('rrws-lock');
    if (!fromPop && this.pushed) { try { history.back(); } catch (e) {} }
    this.pushed = false;
    this.active = -1;
    this.lastFocus?.focus?.({ preventScroll: true });
    // resume visible cards
    this.track.querySelectorAll('.rrws__vid').forEach((v) => {
      const r = v.getBoundingClientRect();
      if (r.top < innerHeight && r.bottom > 0 && r.left < innerWidth && r.right > 0 && v.getAttribute('src')) safePlay(v);
    });
  }

  go(i) {
    if (i < 0 || i >= this.slides.length) return;
    this.feed.scrollTo({ top: this.slides[i].offsetTop, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  activate(i) {
    if (i === this.active || this.player.hidden) return;
    this.active = i;
    this.slides.forEach((s, n) => {
      const v = s.querySelector('video');
      if (n === i) {
        loadSrc(v);
        v.muted = this.muted;
        v.currentTime = 0;
        safePlay(v, true);
        s.querySelector('[data-rrws-mute]')?.setAttribute('aria-pressed', String(this.muted));
      } else {
        if (Math.abs(n - i) === 1) { v.preload = 'auto'; loadSrc(v); }
        v.pause();
      }
    });
    if (this.pushed) { try { history.replaceState({ rrws: true }, '', this.urlFor(this.slides[i])); } catch (e) {} }
    this.tick();
  }

  tick() {
    cancelAnimationFrame(this.raf);
    const slide = this.slides[this.active];
    if (!slide) return;
    const v = slide.querySelector('video');
    const bar = slide.querySelector('.rrws-p__prog i');
    const loop = () => {
      if (v.duration) bar.style.width = (v.currentTime / v.duration) * 100 + '%';
      this.raf = requestAnimationFrame(loop);
    };
    loop();
  }

  toggleMute() {
    this.muted = !this.muted;
    const slide = this.slides[this.active];
    const v = slide?.querySelector('video');
    if (v) { v.muted = this.muted; if (!this.muted && v.paused) safePlay(v); }
    this.slides.forEach((s) => s.querySelector('[data-rrws-mute]')?.setAttribute('aria-pressed', String(this.muted)));
  }

  urlFor(slide) {
    const u = new URL(location.href);
    u.searchParams.set('reel', slide.dataset.reel);
    return u.toString();
  }

  async share() {
    const slide = this.slides[this.active];
    if (!slide) return;
    const url = this.urlFor(slide);
    const title = slide.querySelector('.rrws-p__ptitle')?.textContent?.trim() || document.title;
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(url); this.toast('Link copied'); }
    catch (e) { this.toast(url); }
  }

  toast(msg) {
    if (!this.toastEl) return;
    this.toastEl.textContent = msg;
    this.toastEl.hidden = false;
    clearTimeout(this.toastT);
    this.toastT = setTimeout(() => { this.toastEl.hidden = true; }, 2200);
  }

  openFromUrl() {
    const id = new URLSearchParams(location.search).get('reel');
    if (!id) return;
    const idx = this.slides.findIndex((s) => s.dataset.reel === id);
    if (idx < 0) return;
    // strip the param from the base entry so closing returns to a clean URL
    try {
      const u = new URL(location.href); u.searchParams.delete('reel');
      history.replaceState(history.state, '', u.toString());
    } catch (e) {}
    this.open(idx);
  }

  trapFocus(e) {
    const f = Array.from(this.player.querySelectorAll('button:not([disabled]), a[href]'))
      .filter((el) => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- cart ---------- */
  async addToCart(btn) {
    if (btn.disabled) return;
    const variantId = Number(btn.dataset.rrwsAtc);
    const label = btn.dataset.label || btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId).filter(Boolean);
    const events = await getEvents();
    let deferred = null;
    if (events && events.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      }));
    }

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (data.status) {
        deferred?.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'rr-watch-shop' } });
        this.toast(data.description || data.message || 'Could not add to cart');
        btn.disabled = false; btn.textContent = label;
        return;
      }

      if (deferred) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'rr-watch-shop', itemCount: 1, sections: data.sections, didError: false },
        });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      btn.textContent = 'Added';
      setTimeout(() => { btn.disabled = false; btn.textContent = label; }, 1800);

      // open the theme's cart drawer
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') {
        if (!drawer) window.location.href = root + 'cart';
        return;
      }
      const overlayMode = window.matchMedia('(max-width: 989px)').matches;
      setTimeout(() => {
        if (overlayMode) {
          // drawer opens as a modal above the player; resume the video when it closes
          this.slides[this.active]?.querySelector('video')?.pause();
          drawer.addEventListener('theme-drawer:close', () => {
            if (!this.player.hidden) safePlay(this.slides[this.active]?.querySelector('video'), true);
          }, { once: true });
        } else {
          // on desktop the drawer sits beside the page, so step out of the player first
          this.close();
        }
        if (!drawer.isOpen) drawer.open();
      }, 60);
    } catch (err) {
      deferred?.reject(err);
      this.toast('Could not add to cart. Please try again.');
      btn.disabled = false; btn.textContent = label;
    }
  }
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-rrws]').forEach((el) => {
    if (el.__rrws) return;
    el.__rrws = new WatchShop(el);
  });
}
boot();

// theme editor: re-init on section load, clean up orphaned players on unload
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  const ws = e.target.querySelector('[data-rrws]');
  if (ws && ws.__rrws && ws.__rrws.player) {
    ws.__rrws.player.remove();
    document.documentElement.classList.remove('rrws-lock');
  }
});
